import type {
  AttachmentAdapter,
  PendingAttachment,
  CompleteAttachment,
} from "@assistant-ui/react";
import { apiAuthFetch } from "@/lib/authFetch";
import { ClientLogger } from "@/lib/clientLogger";
import { getAuiRemoteThreadId } from "@/lib/auiThreadIdBridge";
import { toast } from "sonner";
import i18n from "@/lib/i18n";

/**
 * Map a file's extension / MIME type to an assistant-ui attachment "type" bucket.
 * Drives the chip's visual variant (Image / Document / File). Our supported V1 set
 * is text-y, so we never report "image" — but the function is liberal so a future
 * image extractor automatically renders the right tile.
 */
function inferAttachmentType(file: File): "image" | "document" | "file" {
  if (file.type.startsWith("image/")) return "image";
  const ext = file.name.toLowerCase().split(".").pop();
  if (ext === "docx" || ext === "pdf" || ext === "md" || ext === "txt") return "document";
  return "file";
}

/**
 * Waits up to `timeoutMs` for a non-null thread id to appear. Checks both the
 * route-context closure and the AUI bridge (module-scoped fast path set by
 * AuiThreadIdBridge's useEffect after initialize() resolves). Resolves as soon
 * as either source publishes a real GUID; rejects if the timeout elapses.
 */
function waitForThreadId(
  getThreadId: () => string | null,
  timeoutMs = 10_000,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    const check = () => {
      const fromClosure = getThreadId();
      const fromBridge = getAuiRemoteThreadId();
      const id = fromClosure ?? fromBridge ?? null;
      if (id && !id.startsWith("__LOCAL")) {
        resolve(id);
        return;
      }
      if (Date.now() > deadline) {
        reject(new Error("Attachment send timed out waiting for thread id"));
        return;
      }
      setTimeout(check, 25);
    };
    check();
  });
}

/**
 * Factory for the praetor-backed attachment adapter. The adapter follows the
 * upload-then-reference pattern: `add` only stages the file on the client
 * (no server round-trip) until the user hits Send; `send` then POSTs the
 * multipart upload to <c>/api/threads/{threadId}/attachments</c> and the
 * server-assigned id flows into the chat-message body. `remove` only DELETEs
 * server-side if the attachment is already complete — pending files were never
 * uploaded.
 *
 * `getThreadId` is a closure over the live route context so the adapter always
 * reads the current thread at call time (assistant-ui keeps the adapter ref
 * stable across thread switches). A null thread id means "the composer is on a
 * new, unmaterialised thread" — `send` waits for thread initialization to
 * complete (up to 10s) rather than immediately failing, since assistant-ui
 * calls `initialize()` before `send()` but the route context and AUI bridge
 * update asynchronously via React effects.
 */
export function createPraetorAttachmentAdapter(
  getThreadId: () => string | null,
): AttachmentAdapter {
  return {
    // Same set the backend's IDocumentContentExtractor supports — keep these
    // in lockstep so the file picker never lets through something the server
    // would 415 on. Native HTML accept string (mime / ext list).
    accept: ".docx,.pdf,.txt,.md",

    async add({ file }): Promise<PendingAttachment> {
      // No server hop yet — assistant-ui calls `send` later (on Send press).
      // The local id is replaced by the server's GUID inside `send`.
      const tempId = (globalThis.crypto?.randomUUID?.() ?? `tmp-${Date.now()}-${Math.random()}`);
      return {
        id: tempId,
        type: inferAttachmentType(file),
        name: file.name,
        contentType: file.type || undefined,
        file,
        status: { type: "requires-action", reason: "composer-send" },
      };
    },

    async send(pending: PendingAttachment): Promise<CompleteAttachment> {
      let threadId = getThreadId();
      if (!threadId) {
        // Thread not yet materialised — initialize() ran but the route context /
        // AUI bridge haven't propagated the new id yet. Wait for it (up to 10s).
        try {
          threadId = await waitForThreadId(getThreadId);
        } catch {
          const err = new Error(
            "Attachment send failed: thread did not materialise within timeout.",
          );
          ClientLogger.error("Praetor attachment send aborted: threadId wait timed out", {
            component: "praetorAttachmentAdapter",
            metadata: { fileName: pending.name, size: pending.file.size },
          });
          throw err;
        }
      }

      const form = new FormData();
      form.append("file", pending.file, pending.name);

      const res = await apiAuthFetch(
        `api/threads/${threadId}/attachments`,
        { method: "POST", body: form },
      );

      if (!res.ok) {
        // Body is { Message, Extension? } on 4xx. Bubble enough up so the chip
        // shows an actionable label when assistant-ui renders the failure.
        let bodyMsg = `HTTP ${res.status}`;
        try {
          const body = await res.json();
          if (body?.Message) bodyMsg = String(body.Message);
        } catch { /* non-JSON body — keep status-only message */ }
        ClientLogger.error("Praetor attachment upload failed", {
          component: "praetorAttachmentAdapter",
          metadata: { threadId, status: res.status, fileName: pending.name, message: bodyMsg },
        });
        throw new Error(bodyMsg);
      }

      const dto = await res.json() as {
        id: string;
        fileName: string;
        contentType: string;
        extension: string;
        size: number;
        uploadedAt: string;
        warning?: string | null;
      };

      if (dto.warning) {
        toast.warning(dto.warning ?? i18n.t("attachments.noExtractableText"));
      }

      return {
        id: dto.id,
        type: pending.type,
        name: dto.fileName,
        contentType: dto.contentType,
        // V1: no inline content delivered to the LLM. The agent lazy-loads via
        // the read_attachment native tool — the placeholder marker injected on
        // the user turn carries the attachment id and metadata for it to use.
        content: [],
        status: { type: "complete" },
      };
    },

    async remove(attachment): Promise<void> {
      const threadId = getThreadId();
      // Pending attachments were never uploaded; nothing to delete server-side.
      if (attachment.status.type !== "complete") return;
      if (!threadId) return; // defensive — shouldn't happen if it's complete

      try {
        const res = await apiAuthFetch(
          `api/threads/${threadId}/attachments/${attachment.id}`,
          { method: "DELETE" },
        );
        if (!res.ok && res.status !== 404) {
          // 404 is fine (already gone). Anything else logged for diagnostic
          // — we don't surface it to the user because removal is a UI op
          // and the chip is already gone from the composer.
          ClientLogger.warn("Praetor attachment remove returned non-OK", {
            component: "praetorAttachmentAdapter",
            metadata: { threadId, attachmentId: attachment.id, status: res.status },
          });
        }
      } catch (err) {
        ClientLogger.warn("Praetor attachment remove threw", {
          component: "praetorAttachmentAdapter",
          metadata: { threadId, attachmentId: attachment.id },
          error: err as Error,
        });
      }
    },
  };
}
